<template>
  <FormStep
    title="Billing Information"
    @submit="handleSubmit"
  >
    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />

    <BaseInlineNotification
      v-if="submissionError"
      heading-text="An Error Occurred"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="submissionError"
      />
    </BaseInlineNotification>

    <div class="flex flex-col gap-y-4">
      <BaseInlineNotification
        v-if="isFreeOrder"
        heading-text="No Payment Required"
        hide-title-element
        type="info"
      >
        <p
          class="body-sm"
          v-text="'Your discount code covers the full cost of this order. No payment information is necessary to complete your registration.'"
        />
      </BaseInlineNotification>

      <template v-else>
        <fieldset class="flex flex-col gap-y-4">
          <legend class="mb-3 text-sm font-semibold text-gray-800">
            <span v-text="'Select Your Payment Method'" />
          </legend>

          <div class="flex flex-col sm:flex-row gap-4">
            <label
              :class="[
                values.paymentMethod === 'creditCard'
                  ? 'border-2 border-gray-800'
                  : 'border border-gray-200 hover:bg-gray-50'
              ]"
              class="flex items-center rounded-lg px-4 py-3 cursor-pointer transition-colors"
              for="credit_card"
            >
              <input
                id="credit_card"
                :checked="values.paymentMethod === 'creditCard'"
                class="mr-3"
                name="paymentMethod"
                type="radio"
                value="creditCard"
                @change="setFieldValue('paymentMethod', 'creditCard')"
              >
              <span
                class="font-medium"
                v-text="'Credit Card'"
              />
            </label>

            <label
              v-if="hasStatesForBilling"
              :class="[
                values.paymentMethod === 'affirm'
                  ? 'border-2 border-gray-800'
                  : 'border border-gray-200 hover:bg-gray-50'
              ]"
              class="flex items-center border rounded-lg px-4 py-3 cursor-pointer transition-colors"
              for="affirm"
            >
              <input
                id="affirm"
                :checked="values.paymentMethod === 'affirm'"
                class="mr-3"
                name="paymentMethod"
                type="radio"
                value="affirm"
                @change="setFieldValue('paymentMethod', 'affirm')"
              >
              <IconAffirmLogo />
              <span
                class="sr-only"
                v-text="'Affirm'"
              />
            </label>
          </div>
        </fieldset>

        <BaseTextInput
          id="creditCardNumber"
          v-model="values.creditCardNumber"
          v-maska="'#### #### #### ####'"
          autocomplete="cc-number"
          :disabled="disableFormField"
          label="Credit Card Number"
          name="creditCardNumber"
          required
          validation-rules="required"
        />

        <BaseTextInput
          id="expirationDate"
          v-model="values.expirationDate"
          v-maska="'##/####'"
          autocomplete="cc-exp"
          :disabled="disableFormField"
          helper-text-id="expiration-date-helper-text"
          label="Expiration Date"
          name="expirationDate"
          required
          validation-rules="required"
        >
          <template #helperText>
            <p v-text="'Expiration date is in MM/YYYY format'" />
          </template>
        </BaseTextInput>

        <BaseTextInput
          id="ccv"
          v-model="values.ccv"
          v-maska="'####'"
          autocomplete="cc-csc"
          :disabled="disableFormField"
          helper-text-id="ccv-helper-text"
          label="CCV"
          name="ccv"
          required
          validation-rules="required"
        >
          <template #helperText>
            <p v-text="'3 or 4 digit number on back of card'" />
          </template>
        </BaseTextInput>

        <BaseTextInput
          id="nameOnCard"
          v-model="values.nameOnCard"
          :disabled="disableFormField"
          autocomplete="cc-name"
          label="Name on Credit Card"
          name="nameOnCard"
          required
          validation-rules="required"
        />

        <BaseCheckboxInput
          id="sameAsStudentAddress"
          class="my-4 pl-3"
          v-model="values.sameAsStudentAddress"
          :disabled="disableFormField"
          :input-value="values.sameAsStudentAddress"
          label="Billing same as student address?"
          name="sameAsStudentAddress"
          @change="handleSameAddressToggle"
        />

        <template v-if="!values.sameAsStudentAddress">
          <BaseCountrySelect
            v-model="values.country"
            :disabled="disableFormField"
            label="Country"
          />

          <BaseTextInput
            id="address"
            v-model="values.address"
            :disabled="disableFormField"
            autocomplete="street-address"
            helper-text-id="street-address-helper-text"
            label="Address"
            name="address"
            required
            validation-rules="required"
          >
            <template #helperText>
              <p v-text="'Home address required for CE credit'" />
            </template>
          </BaseTextInput>

          <BaseTextInput
            id="addressTwo"
            v-model="values.addressTwo"
            :disabled="disableFormField"
            label="Address Line 2"
            name="addressTwo"
          />

          <BaseTextInput
            id="city"
            v-model="values.city"
            :disabled="disableFormField"
            label="City"
            name="city"
            required
            validation-rules="required"
          />

          <BaseStateSelect
            v-model="values.state"
            :country-id="values.country"
            :disabled="disableFormField"
            :required="hasStatesForBilling"
            :validation-rules="hasStatesForBilling ? 'required' : ''"
            @clear-state="setFieldValue('state', '')"
          />

          <BaseTextInput
            id="postcode"
            v-model="values.postcode"
            v-maska="'#####'"
            :disabled="disableFormField"
            autocomplete="postal-code"
            inputmode="numeric"
            label="Zip Code"
            name="postcode"
            required
            validation-rules="required"
          />
        </template>

        <BaseCheckboxInput
          id="agreeToHaveCreditCardCharged"
          v-model="values.agreeToHaveCreditCardCharged"
          :disabled="disableFormField"
          :input-value="values.agreeToHaveCreditCardCharged"
          class="pl-3"
          label="I agree to have my credit card charged now for the amount entered."
          name="agreeToHaveCreditCardCharged"
          required
          validation-rules="required"
        />
      </template>
    </div>

    <template #footer>
      <div class="flex justify-end gap-x-2 p-4">
        <button
          :disabled="isSubmitting || isLoading || (!isFreeOrder && !values.agreeToHaveCreditCardCharged)"
          class="btn btn--primary"
          type="submit"
        >
          <span class="relative inline-flex items-center justify-center">
            <BaseLoadingIcon :visible="isLoading" />
            <span
              class="whitespace-nowrap"
              :class="{ 'invisible': isLoading }"
              v-text="isFreeOrder ? 'Complete Registration' : 'Submit Payments'"
            />
          </span>
        </button>
      </div>
    </template>
  </FormStep>
</template>

<script lang="ts" setup>
import { vMaska } from 'maska/vue'
import { useForm } from 'vee-validate'
import { computed, onBeforeMount, ref, watch } from 'vue'

import IconAffirmLogo from '@js/components/icons/IconAffirmLogo.vue'
import FormStep from '@js/components/checkout/partials/FormStep.vue'
import BaseStateSelect from '@js/components/global/forms/BaseStateSelect.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseCountrySelect from '@js/components/global/forms/BaseCountrySelect.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BaseCheckboxInput from '@js/components/global/forms/inputs/BaseCheckboxInput.vue'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'
import BaseSelectInput from '@js/components/global/forms/inputs/BaseSelectInput.vue'

import { env } from '@js/utils/env'
import { log } from '@js/utils/logger'
import useCart from '@js/composables/useCart'
import { placeOrder } from '@js/api/checkout/placeOrder'
import { initiateAffirmCheckout } from '@js/utils/affirmHelpers'
import { addQueryParamToURL } from '@js/utils/addQueryParamToURL'
import { setPaymentMethodOnCart } from '@js/api/cart/setPaymentMethodOnCart'

import { Cart } from '@js/api/types/cart/Cart'
import { Customer } from '@js/api/types/customer/Customer'
import { AuthorizeDispatchResponse } from '@js/types/AuthorizeNet'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'

import { useUserStore } from '@js/stores/userStore'

const props = defineProps<{
  cart: Cart | null
  user: Customer | null
  registration: CustomerRegistration | null
}>()

const emit = defineEmits<{
  nextStep: [orderNumber?: string]
}>()

const { setCartInStorage } = useCart()
const userStore = useUserStore()

const {
  errors,
  values,
  resetForm,
  submitCount,
  isSubmitting,
  setFieldValue,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    paymentMethod: 'creditCard',
    payingBy: 'myself',
    creditCardNumber: '',
    expirationDate: '',
    ccv: '',
    nameOnCard: '',
    emailOfCardHolder: '',
    sameAsStudentAddress: false,
    country: 'US',
    address: '',
    addressTwo: '',
    city: '',
    state: '',
    postcode: '',
    agreeToHaveCreditCardCharged: false,
  },
})

const isLoading = ref(false)
const submissionError = ref('')

const disableFormField = computed(() => values.paymentMethod !== 'creditCard')

const isFreeOrder = computed(() => props.cart?.prices.grand_total.value === 0)

const hasStatesForBilling = computed(() => props.registration?.personal.state ? true : false)

const handleSameAddressToggle = (): void => {
  const personalInfo = props.registration?.personal

  if (personalInfo) {
    setFieldValue('country', values.sameAsStudentAddress ? (personalInfo.country || 'US') : 'US')
    setFieldValue('address', values.sameAsStudentAddress ? (personalInfo.address || '') : '')
    setFieldValue('addressTwo', values.sameAsStudentAddress ? (personalInfo.address_two || '') : '')
    setFieldValue('city', values.sameAsStudentAddress ? (personalInfo.city || '') : '')
    setFieldValue('state', values.sameAsStudentAddress ? (personalInfo.state || '') : '')
    setFieldValue('postcode', values.sameAsStudentAddress ? (personalInfo.postcode || '') : '')
  } else {
    setFieldValue('country', 'US')
    setFieldValue('address', '')
    setFieldValue('addressTwo', '')
    setFieldValue('city', '')
    setFieldValue('state', '')
    setFieldValue('postcode', '')
  }
}

const sendDataToAuthorizeNet = (): void => {
  const expirationMonth = values.expirationDate.split('/')[0]
  const expirationFullYear = values.expirationDate.split('/')[1]
  const cardNumber = values.creditCardNumber.replace(/\s/g, '')

  const secureData = {
    authData: {
      clientKey: env.authorizeNet.CLIENT_KEY,
      apiLoginID: env.authorizeNet.API_LOGIN_ID,
    },
    cardData: {
      cardNumber,
      month: expirationMonth,
      year: expirationFullYear.slice(-2),
      cardCode: values.ccv,
    },
  }

  window.Accept.dispatchData(secureData, async (response: AuthorizeDispatchResponse): Promise<void> => {
    isLoading.value = true

    if (response.messages.resultCode === 'Error') {
      submissionError.value = 'Your payment information could not be added.'
      isLoading.value = false
      return
    }

    try {
      const updatedCart = await setPaymentMethodOnCart({
        cartId: props.cart?.id ?? '',
        paymentMethod: {
          code: 'authnetcim',
          tokenbase_data: {
            cc_owner: values.nameOnCard,
            cc_exp_month: expirationMonth,
            cc_exp_year: expirationFullYear,
            cc_last4: values.creditCardNumber.slice(-4),
            cc_cid: values.ccv,
            acceptjs_key: response.opaqueData?.dataDescriptor as string,
            acceptjs_value: response.opaqueData?.dataValue as string,
          },
        },
      })

      if (updatedCart) {
        setCartInStorage(updatedCart)
      }

      const orderNumber = await placeOrder({
        quantity: 1,
        cartId: props.cart?.id ?? '',
        sku: props.registration?.item.sku ?? '',
        registrationId: props.registration?.registration_id ?? 0,
        courseOption: props.registration?.item.course_option ?? '',
        paying_by: values.payingBy,
      })

      if (orderNumber) {
        // Update incomplete registrations count in the global user store
        userStore.decrementIncompleteRegistrationsCount()

        emit('nextStep', String(orderNumber))
      } else {
        submissionError.value = 'An error occurred while processing your order.'
        isLoading.value = false

        log({
          message: 'Error placing order for Authorize.net',
          file: 'src/wcei/js/components/checkout/partials/StepBillingInformation.vue',
        })
      }
    } catch (error) {
      submissionError.value = 'An error occurred while processing your payment.'
      isLoading.value = false

      log({
        message: 'Error setting payment method on cart for Authorize.net',
        file: 'src/wcei/js/components/checkout/partials/StepBillingInformation.vue',
      })
    } finally {
      resetForm()

      isLoading.value = false
    }
  })
}

const handleFreeOrderPlacement = async (): Promise<void> => {
  if (!props.cart?.id) return

  isLoading.value = true
  submissionError.value = ''

  try {
    await setPaymentMethodOnCart({
      cartId: props.cart.id,
      paymentMethod: { code: 'free' },
    })

    const orderNumber = await placeOrder({
      quantity: 1,
      cartId: props.cart.id,
      sku: props.registration?.item.sku ?? '',
      registrationId: props.registration?.registration_id ?? 0,
      courseOption: props.registration?.item.course_option ?? '',
      paying_by: 'myself',
    })

    if (orderNumber) {
      // Update incomplete registrations count in the global user store
      userStore.decrementIncompleteRegistrationsCount()

      emit('nextStep', String(orderNumber))
    } else {
      submissionError.value = 'An error occurred while processing your order.'

      log({
        message: 'Error placing free order',
        file: 'src/wcei/js/components/checkout/partials/StepBillingInformation.vue',
      })
    }
  } catch (error) {
    submissionError.value = 'An error occurred while processing your order.'

    log({
      message: 'Error placing free order',
      file: 'src/wcei/js/components/checkout/partials/StepBillingInformation.vue',
    })
  } finally {
    isLoading.value = false
  }
}

const handleAffirmCheckout = async (): Promise<void> => {
  if (!props.user || !props.cart) {
    return
  }

  submissionError.value = ''
  isLoading.value = true
  
  initiateAffirmCheckout({
    cart: props.cart,
    user: props.user,
    registration: props.registration,
    payingBy: 'myself',
    urls: {
      cancel: `${window.location.origin}?step=2`,
      confirmation: `${window.location.origin}?step=2`,
    },
    onSuccess: (orderNumber: string): void => {
      isLoading.value = false

      // Update incomplete registrations count in the global user store
      userStore.decrementIncompleteRegistrationsCount()

      emit('nextStep', orderNumber)
    },
    onError: (errorMessage: string): void => {
      isLoading.value = false
      submissionError.value = errorMessage
    },
  })
}

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  if (isFreeOrder.value) {
    await handleFreeOrderPlacement()
    return
  }

  if (values.paymentMethod === 'creditCard') {
    isLoading.value = true
    sendDataToAuthorizeNet()
  }
})

watch(
  () => values.paymentMethod,
  (newValue) => {
    submissionError.value = ''

    if (newValue === 'affirm') {
      handleAffirmCheckout()
    }
  },
  { immediate: true },
)

onBeforeMount(() => {
  addQueryParamToURL('step', '2')
})
</script>
